import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { LayoutGrid, SlidersHorizontal, Plus, Image, Link2, Tag, Inbox, X, CheckSquare, Square, Trash2, FolderPlus, Layers, ChevronRight, ChevronDown, Check, Pin, Waypoints, ArrowDownWideNarrow, ArrowDownToLine } from "lucide-react";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { CardDetailSheet } from "./CardDetailSheet";
import { CollectionPicker } from "./CollectionPicker";
import { ThoughtCard, type Thought } from "./ThoughtCard";
import { useBurrow } from "./BurrowStore";
import { InlineBurrowLoader } from "./BurrowLoader";
import { ThreadSidebar } from "./ThreadSidebar";
import { ConfirmDialog } from "./ConfirmDialog";
import { useNavigate } from "react-router";
import { QuickAddThoughtModal } from "./QuickAddThoughtModal";

/* ─── Custom Select Dropdown ─── */
function CustomSelect({
  value,
  onChange,
  options,
  className = "",
}: {
  value: string;
  onChange: (value: string) => void;
  /** `short` is the compact trigger label; the dropdown always shows `label`. */
  options: { value: string; label: string; short?: string }[];
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  // Fixed-position coords for the menu — the trigger lives inside a
  // horizontally scrollable chip row on mobile, whose overflow would clip an
  // absolutely-positioned menu.
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const close = () => setOpen(false);
    document.addEventListener("mousedown", handler);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", handler);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <div ref={ref} className={`relative shrink-0 ${className}`}>
      <button
        onClick={() => {
          if (!open && ref.current) {
            const r = ref.current.getBoundingClientRect();
            setMenuPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
          }
          setOpen((v) => !v);
        }}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border whitespace-nowrap transition-all ${
          open
            ? "border-accent/30 bg-card text-foreground"
            : "border-border/40 bg-card/60 text-muted-foreground hover:text-foreground hover:border-border/60"
        }`}
        style={{ fontSize: "0.82rem", fontFamily: "'Fraunces', Georgia, serif" }}
        title="Sort"
      >
        <ArrowDownWideNarrow size={13} className="text-muted-foreground/50 shrink-0" />
        {selected?.short ?? selected?.label}
        <ChevronDown
          size={13}
          className={`text-muted-foreground/50 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="z-50 min-w-[160px] bg-card border border-border/40 rounded-xl shadow-xl shadow-foreground/8 overflow-hidden py-1"
            style={{ position: "fixed", top: menuPos?.top, right: menuPos?.right }}
          >
            {options.map((opt) => (
              <button
                key={opt.value}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className={`w-full text-left px-3.5 py-2 flex items-center justify-between gap-3 transition-colors ${
                  opt.value === value
                    ? "text-accent bg-accent/5"
                    : "text-foreground/70 hover:bg-muted/30 hover:text-foreground"
                }`}
                style={{ fontSize: "0.82rem", fontFamily: "'Fraunces', Georgia, serif" }}
              >
                {opt.label}
                {opt.value === value && <Check size={13} className="text-accent shrink-0" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

type FilterKey = "all" | "loose" | "has_source" | "borrowed" | "has_image" | "connected" | string;

interface FilterPill {
  key: FilterKey;
  label: string;
  icon?: React.ComponentType<{ size: number }>;
}

const FILTERS: FilterPill[] = [
  { key: "all", label: "All" },
  { key: "loose", label: "Loose Thoughts", icon: Inbox },
  { key: "has_source", label: "Source", icon: Link2 },
  { key: "borrowed", label: "Borrowed", icon: ArrowDownToLine },
  { key: "has_image", label: "Images", icon: Image },
  { key: "connected", label: "Connected", icon: Link2 },
  { key: "in_thread", label: "Trails", icon: Layers },
];

type SortKey = "newest" | "oldest" | "most_connected";

export function LibraryPage() {
  const { state, deleteThought, loadMoreThoughts, loadAllThoughts, loadAllLabels, setActiveThread, deleteThread, addThought, showToast } = useBurrow();
  const [showAddModal, setShowAddModal] = useState(false);
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<FilterKey>("all");
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortKey>("newest");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  const [showBulkCollectionPicker, setShowBulkCollectionPicker] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ type: "bulk" | "single" | "thread"; id?: string } | null>(null);

  // Library-specific pins (separate from home page pins)
  const [libraryPinnedIds, setLibraryPinnedIds] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem("burrow:pinned-library");
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });

  const toggleLibraryPin = useCallback((id: string) => {
    setLibraryPinnedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      localStorage.setItem("burrow:pinned-library", JSON.stringify([...next]));
      return next;
    });
  }, []);

  const thoughts = state.thoughts;
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          void loadMoreThoughts({ limit: 120 });
        }
      },
      { rootMargin: "800px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loadMoreThoughts]);

  // Filtering and non-default sorts are only meaningful over the whole library:
  // "oldest" across the newest page returns the newest cards, and a tag filter
  // silently omits every match that hasn't been scrolled into memory. Both look
  // like complete answers, so pull the full set in before answering at all.
  // "Trails" is excluded because it isn't a card filter at all — it swaps the
  // grid for the saved-trail list, so pulling the library in would be pure waste.
  const needsFullSet =
    (activeFilter !== "all" && activeFilter !== "in_thread") ||
    activeTags.length > 0 ||
    sortBy !== "newest";
  useEffect(() => {
    if (state.hydrated && needsFullSet) void loadAllThoughts();
  }, [state.hydrated, needsFullSet, loadAllThoughts]);

  // The tag vocabulary comes from the server so every tag is offered as a chip,
  // not just those on loaded cards. Until it arrives, fall back to what's in
  // memory so the row isn't empty on first paint.
  useEffect(() => {
    if (state.hydrated) void loadAllLabels();
  }, [state.hydrated, loadAllLabels]);

  const ALL_TAGS = useMemo(() => {
    if (state.allLabelsLoaded) return state.allLabels;
    return Array.from(new Set(thoughts.flatMap((t) => t.labels ?? []))).filter(Boolean);
  }, [state.allLabels, state.allLabelsLoaded, thoughts]);

  /** Results can still grow — say so instead of showing a confident wrong count.
   *  Tied to the in-flight flag, not just to completeness, so a failed load
   *  falls back to reporting what's actually here rather than hanging. */
  const filtersPending = needsFullSet && !state.thoughtsAllLoaded && state.thoughtsLoadingAll;

  const filtered = useMemo(() => {
    let result = [...thoughts];
    if (activeFilter === "loose") {
      // Loose = untagged, not in a collection, and not part of a trail/thread
      // (cards in a thread already have connections, so they aren't loose).
      const collectedIds = new Set(state.collections.flatMap((c) => c.card_ids));
      result = result.filter(
        (t) => (t.labels ?? []).length === 0 && !collectedIds.has(t.id) && !t.in_thread
      );
    }
    if (activeFilter === "has_source") result = result.filter((t) => t.source_url);
    // Borrowed = came from someone else's card. `source_card_id` is the borrow
    // pointer, distinct from `source_url` (a link you saved yourself).
    if (activeFilter === "borrowed") result = result.filter((t) => t.source_card_id);
    if (activeFilter === "has_image") result = result.filter((t) => t.image_path || t.image_url);
    if (activeFilter === "connected") result = result.filter((t) => t.connections > 0);
    if (activeTags.length > 0) {
      result = result.filter((t) => activeTags.some((tag) => (t.labels ?? []).includes(tag)));
    }
    if (sortBy === "newest") result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    if (sortBy === "oldest") result.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    if (sortBy === "most_connected") result.sort((a, b) => b.connections - a.connections);
    // Pinned cards float to top
    const pinned = result.filter((t) => libraryPinnedIds.has(t.id));
    const unpinned = result.filter((t) => !libraryPinnedIds.has(t.id));
    return [...pinned, ...unpinned];
  }, [thoughts, activeFilter, activeTags, sortBy, libraryPinnedIds, state.collections]);

  const selectedThought = selectedId ? thoughts.find((t) => t.id === selectedId) ?? null : null;

  const toggleTag = (tag: string) => {
    setActiveTags((prev) => prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]);
  };

  const openThread = (threadId: string) => {
    setSelectedThreadId(threadId);
  };

  const toggleBulk = (id: string) => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = () => {
    bulkSelected.forEach((id) => deleteThought(id));
    setBulkSelected(new Set());
    setBulkMode(false);
  };

  // Select all applies to the currently filtered/visible set.
  const allFilteredSelected = filtered.length > 0 && filtered.every((t) => bulkSelected.has(t.id));

  const toggleSelectAll = () => {
    setBulkSelected(allFilteredSelected ? new Set() : new Set(filtered.map((t) => t.id)));
  };

  const handleCardClick = (id: string) => {
    if (bulkMode) {
      toggleBulk(id);
    } else {
      setSelectedId(id);
    }
  };

  return (
    <div className="min-h-dvh bg-background">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        {/* Page header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex flex-row items-start sm:items-end justify-between gap-4 mb-8"
        >
          <div>
            <h1
              className="text-primary"
              style={{
                fontFamily: "'Fraunces', Georgia, serif",
                fontSize: "clamp(1.6rem, 3.5vw, 2.2rem)",
                fontWeight: 400,
              }}
            >
              Library
            </h1>
            <p className="text-muted-foreground mt-1" style={{ fontSize: "0.88rem" }}>
              {/* Filtering narrows the headline to the match count. While the
                  full set is still arriving that count is provisional, so it's
                  marked as such rather than presented as final. */}
              {needsFullSet
                ? filtersPending
                  ? "Searching your library…"
                  : `${filtered.length} of ${state.thoughtsTotal} thoughts`
                : `${state.thoughtsTotal} thoughts collected`}
            </p>
          </div>

          <div className="flex items-center gap-2 self-start sm:self-auto">
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl border border-border/40 text-muted-foreground hover:text-foreground hover:border-accent/40 hover:bg-muted/20 transition-all bg-card/60"
              style={{ fontSize: "0.82rem" }}
            >
              <Plus size={15} />
              <span className="hidden sm:inline">Add card</span>
            </button>
            <button
              onClick={() => { setBulkMode(!bulkMode); setBulkSelected(new Set()); }}
              className={`px-3.5 py-2.5 rounded-xl border transition-all flex items-center gap-2 ${
                bulkMode
                  ? "bg-accent/15 border-accent/30 text-accent"
                  : "border-border/40 text-muted-foreground hover:text-foreground bg-card/60"
              }`}
              style={{ fontSize: "0.82rem" }}
            >
              {bulkMode ? <CheckSquare size={15} /> : <Square size={15} />}
              {bulkMode ? "Done" : "Select"}
            </button>
          </div>
        </motion.div>

        {/* Bulk actions bar */}
        <AnimatePresence>
          {bulkMode && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="mb-4 flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-3 rounded-2xl bg-card border border-accent/20"
            >
              <button
                onClick={toggleSelectAll}
                disabled={filtered.length === 0}
                aria-label={allFilteredSelected ? "Deselect all" : "Select all"}
                className="flex items-center gap-1.5 shrink-0 whitespace-nowrap px-2.5 sm:px-3 py-1.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all disabled:opacity-40 disabled:pointer-events-none"
                style={{ fontSize: "0.82rem" }}
              >
                {allFilteredSelected ? <CheckSquare size={14} /> : <Square size={14} />}
                <span className="hidden sm:inline">{allFilteredSelected ? "Deselect all" : "Select all"}</span>
              </button>
              <div className="hidden sm:block w-px h-5 bg-border/40 mx-1 shrink-0" />
              <span className="text-accent shrink-0 whitespace-nowrap" style={{ fontSize: "0.82rem" }}>
                {bulkSelected.size} selected
              </span>
              <div className="hidden sm:block w-px h-5 bg-border/40 mx-1 shrink-0" />
              <button
                onClick={() => setShowBulkCollectionPicker(true)}
                disabled={bulkSelected.size === 0}
                aria-label="Add to collection"
                title="Add to collection"
                className="flex items-center gap-1.5 shrink-0 whitespace-nowrap px-2.5 sm:px-3 py-1.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all disabled:opacity-40 disabled:pointer-events-none"
                style={{ fontSize: "0.82rem" }}
              >
                <FolderPlus size={14} /> <span className="hidden sm:inline">Add to collection</span>
              </button>
              <button
                onClick={() => {
                  bulkSelected.forEach((id) => {
                    if (!libraryPinnedIds.has(id)) toggleLibraryPin(id);
                  });
                  setBulkSelected(new Set());
                  setBulkMode(false);
                }}
                disabled={bulkSelected.size === 0}
                aria-label="Pin"
                title="Pin"
                className="flex items-center gap-1.5 shrink-0 whitespace-nowrap px-2.5 sm:px-3 py-1.5 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all disabled:opacity-40 disabled:pointer-events-none"
                style={{ fontSize: "0.82rem" }}
              >
                <Pin size={14} /> <span className="hidden sm:inline">Pin</span>
              </button>
              <button
                onClick={() => setConfirmDelete({ type: "bulk" })}
                disabled={bulkSelected.size === 0}
                aria-label="Delete"
                title="Delete"
                className="flex items-center gap-1.5 shrink-0 whitespace-nowrap px-2.5 sm:px-3 py-1.5 rounded-xl text-destructive/70 hover:text-destructive hover:bg-destructive/8 transition-all ml-auto disabled:opacity-40 disabled:pointer-events-none"
                style={{ fontSize: "0.82rem" }}
              >
                <Trash2 size={14} /> <span className="hidden sm:inline">Delete</span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Filter bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mb-6"
        >
          {/* Mobile: one scrollable chip row (no stacking); desktop: wraps as before */}
          <div className="flex items-center gap-2 flex-nowrap overflow-x-auto overflow-y-hidden scrollbar-hide md:flex-wrap md:overflow-visible -mx-4 px-4 md:mx-0 md:px-0">
            {FILTERS.map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl border whitespace-nowrap shrink-0 transition-all duration-200 ${
                  activeFilter === filter.key
                    ? "bg-accent/15 border-accent/30 text-accent"
                    : "border-border/40 text-muted-foreground hover:text-foreground hover:border-border/60 bg-card/60"
                }`}
                style={{ fontSize: "0.82rem" }}
              >
                {filter.icon && <filter.icon size={13} />}
                {filter.label}
              </button>
            ))}

            <div className="w-px h-6 bg-border/40 mx-1 shrink-0" />

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl border whitespace-nowrap shrink-0 transition-all duration-200 ${
                showFilters
                  ? "bg-accent/15 border-accent/30 text-accent"
                  : "border-border/40 text-muted-foreground hover:text-foreground bg-card/60"
              }`}
              style={{ fontSize: "0.82rem" }}
            >
              <SlidersHorizontal size={13} />
              Filters
            </button>

            {/* PRD §4.2 — Map lives as a view inside Library, not as a top-level nav item. */}
            <div className="inline-flex items-center rounded-xl border border-border/40 bg-card/60 p-0.5 ml-1 shrink-0">
              <button
                onClick={() => { /* already on grid */ }}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-accent/15 text-accent transition-all"
                style={{ fontSize: "0.78rem" }}
                title="Grid view"
              >
                <LayoutGrid size={12} />
                Grid
              </button>
              <button
                onClick={() => navigate("/map")}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-muted-foreground hover:text-foreground transition-all"
                style={{ fontSize: "0.78rem" }}
                title="Map view"
              >
                <Waypoints size={12} />
                Map
              </button>
            </div>

            <CustomSelect
              value={sortBy}
              onChange={(v) => setSortBy(v as SortKey)}
              options={[
                { value: "newest", label: "Newest first", short: "Newest" },
                { value: "oldest", label: "Oldest first", short: "Oldest" },
                { value: "most_connected", label: "Most connected", short: "Connected" },
              ]}
              className="ml-auto"
            />
          </div>

          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <div className="pt-4 pb-2">
                  <label className="text-muted-foreground/60 block mb-2" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                    Labels
                  </label>
                  <div className="flex items-center gap-2 flex-wrap">
                    {ALL_TAGS.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => toggleTag(tag)}
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-full border transition-all duration-200 ${
                          activeTags.includes(tag)
                            ? "bg-accent/15 border-accent/30 text-accent"
                            : "border-border/40 text-muted-foreground hover:text-foreground bg-card/40"
                        }`}
                        style={{ fontSize: "0.75rem" }}
                      >
                        <Tag size={10} />
                        {tag}
                      </button>
                    ))}
                    {activeTags.length > 0 && (
                      <button
                        onClick={() => setActiveTags([])}
                        className="text-muted-foreground/50 hover:text-accent flex items-center gap-1 transition-colors"
                        style={{ fontSize: "0.75rem" }}
                      >
                        <X size={12} />
                        Clear labels
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Active tag indicators */}
        {activeTags.length > 0 && (
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="text-muted-foreground/50" style={{ fontSize: "0.78rem" }}>Showing:</span>
            {activeTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-accent/12 text-accent"
                style={{ fontSize: "0.75rem" }}
              >
                {tag}
                <button onClick={() => toggleTag(tag)} className="hover:text-foreground transition-colors">
                  <X size={10} />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Content */}
        {!state.hydrated ? (
          <InlineBurrowLoader theme="search" />
        ) : activeFilter === "in_thread" ? (
          /* Thread list view */
          state.savedThreads.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {state.savedThreads.map((thread, i) => (
                <motion.div
                  key={thread.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: i * 0.04 }}
                  className={`rounded-2xl bg-card p-5 hover:shadow-lg hover:shadow-accent/5 transition-all duration-300 relative overflow-hidden group/thread ${
                    selectedThreadId === thread.id ? "ring-2 ring-accent/30" : ""
                  }`}
                >
                  {/* Accent bar */}
                  <div className="absolute top-0 left-0 w-1 h-full bg-accent/30 rounded-l-2xl" />

                  <div className="flex items-start justify-between gap-3">
                    <button
                      onClick={() => openThread(thread.id)}
                      className="flex-1 text-left min-w-0 pl-2"
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <Layers size={13} className="text-accent/60 shrink-0" />
                        <span className="text-muted-foreground/50" style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                          Trail
                        </span>
                      </div>
                      <p
                        className="text-primary truncate"
                        style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "1rem" }}
                      >
                        {thread.title}
                      </p>
                      <p className="text-muted-foreground/40 mt-1" style={{ fontSize: "0.75rem" }}>
                        {new Date(thread.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </p>
                    </button>
                    <div className="flex items-center gap-1 shrink-0 mt-1">
                      <button
                        onClick={() => setConfirmDelete({ type: "thread", id: thread.id })}
                        className="opacity-0 group-hover/thread:opacity-100 p-1.5 rounded-lg text-muted-foreground/30 hover:text-destructive hover:bg-destructive/8 transition-all"
                      >
                        <Trash2 size={13} />
                      </button>
                      <ChevronRight size={14} className="text-muted-foreground/20" />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
              <Layers size={36} className="text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-muted-foreground/60" style={{ fontSize: "0.95rem" }}>No saved trails yet.</p>
              <p className="text-muted-foreground/40 mt-1" style={{ fontSize: "0.82rem" }}>
                Create a thinking trail in Thinking Space to see it here.
              </p>
            </motion.div>
          )
        ) : filtered.length > 0 ? (
          /* Masonry card layout — same as Continue Thinking on home */
          <ResponsiveMasonry columnsCountBreakPoints={{ 350: 2, 500: 3, 900: 4 }}>
            <Masonry gutter="10px">
              {filtered.map((thought, i) => (
                <div
                  key={thought.id}
                  className={`relative cursor-pointer group/card w-full ${
                    selectedId === thought.id ? "ring-2 ring-accent/20 rounded-2xl" : ""
                  }`}
                  onClick={() => handleCardClick(thought.id)}
                >
                  <ThoughtCard thought={thought} index={i} />
                  {bulkMode ? (
                    /* Bulk select checkbox — top right */
                    <div className="absolute top-2.5 right-2.5 z-10">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleBulk(thought.id); }}
                        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                          bulkSelected.has(thought.id)
                            ? "bg-accent border-accent text-accent-foreground"
                            : "border-muted-foreground/30 bg-card/80 hover:border-accent/50"
                        }`}
                      >
                        {bulkSelected.has(thought.id) && (
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </button>
                    </div>
                  ) : (
                    /* Pin + trash on hover */
                    <div className={`absolute top-2 right-2 z-10 flex items-center gap-1 transition-opacity duration-200 ${
                      libraryPinnedIds.has(thought.id) ? "opacity-100" : "opacity-0 group-hover/card:opacity-100"
                    }`}>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleLibraryPin(thought.id); }}
                        className={`w-6 h-6 flex items-center justify-center rounded-lg transition-all duration-200 ${
                          libraryPinnedIds.has(thought.id)
                            ? "bg-accent text-accent-foreground shadow-sm shadow-accent/20"
                            : "bg-card/90 border border-border/40 text-muted-foreground/40 hover:text-accent hover:border-accent/30"
                        }`}
                        title={libraryPinnedIds.has(thought.id) ? "Unpin" : "Pin to top"}
                      >
                        <Pin size={11} className={libraryPinnedIds.has(thought.id) ? "fill-current" : ""} style={libraryPinnedIds.has(thought.id) ? { transform: "rotate(-45deg)" } : undefined} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete({ type: "single", id: thought.id }); }}
                        className="w-6 h-6 flex items-center justify-center rounded-lg bg-card/90 border border-border/40 text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-all shadow-sm"
                        title="Delete"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </Masonry>
          </ResponsiveMasonry>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <Inbox size={36} className="text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground/60" style={{ fontSize: "0.95rem" }}>
              No thoughts match these filters.
            </p>
            <button
              onClick={() => { setActiveFilter("all"); setActiveTags([]); }}
              className="mt-3 text-accent hover:underline"
              style={{ fontSize: "0.85rem" }}
            >
              Clear all filters
            </button>
          </motion.div>
        )}

        {/* Infinite scroll sentinel */}
        <div className="mt-10 flex items-center justify-center">
          <div ref={loadMoreRef} className="h-10 w-full" />
          {(state.thoughtsLoadingMore || state.thoughtsLoadingAll) && (
            <span className="text-muted-foreground/50" style={{ fontSize: "0.8rem" }}>
              Loading more…
            </span>
          )}
        </div>
      </div>

      {/* Card detail side sheet */}
      {!bulkMode && !selectedThreadId && (
        <CardDetailSheet
          thought={selectedThought}
          onClose={() => setSelectedId(null)}
        />
      )}

      {/* Thread detail sidebar */}
      <ThreadSidebar
        threadId={selectedThreadId}
        onClose={() => setSelectedThreadId(null)}
        onSelectThought={(id) => setSelectedId(id)}
      />

      {/* Bulk add to collection */}
      <CollectionPicker
        open={showBulkCollectionPicker}
        onClose={() => { setShowBulkCollectionPicker(false); setBulkSelected(new Set()); setBulkMode(false); }}
        thoughtIds={Array.from(bulkSelected)}
        title={`Add ${bulkSelected.size} thoughts to collection`}
      />

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={!!confirmDelete}
        title={
          confirmDelete?.type === "bulk"
            ? `Delete ${bulkSelected.size} thought${bulkSelected.size !== 1 ? "s" : ""}?`
            : confirmDelete?.type === "thread"
            ? "Delete this trail?"
            : "Delete this thought?"
        }
        onConfirm={() => {
          if (confirmDelete?.type === "bulk") handleBulkDelete();
          else if (confirmDelete?.type === "thread" && confirmDelete.id) deleteThread(confirmDelete.id);
          else if (confirmDelete?.id) deleteThought(confirmDelete.id);
          setConfirmDelete(null);
        }}
        onCancel={() => setConfirmDelete(null)}
      />

      <QuickAddThoughtModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={(body, title) => {
          addThought(body, title);
          showToast("Thought added.");
        }}
      />
    </div>
  );
}
